import { NodeTagRow } from "@features/administration/products/EditProduct/nodeTagsHelper"
import { Distribution, DistributionParam, Distributions } from "@custom-types/graphs"
import { AppNode, NodeProperties, NodeType } from "@features/builder/graph/types"
import { ValidationState } from "@features/builder/graph/nodes/validation/types"
import { UNBOUNDED_SUPPORT_FLOOR_OFFSET } from "@features/builder/helpers/consts"
import { GraphTagDto } from "@features/builder/types"
import {
  convertNode,
  distributionDefaultValues,
  doesNodeHaveLagParent,
  isPseudoNodeType,
} from "@features/builder/helpers/nodes"
import { NodeFormValues } from "@features/builder/NodeDrawer/NodeForm"
import { decodeNodeUnit, encodeNodeUnit, isValidUnitFormat } from "@utils/format/nodeUnitFormat"
import {
  BaseGraphZipNodeProperties,
  NodeGraphTagDto,
  TKNode,
} from "@store/services/converter/types"
import {
  DistributionMap,
  continuousNodeDistributionProperties,
  discreteNodeDistributionProperties,
} from "@utils/graph"
import { createLagNodeLabel } from "@utils/node"
import { splitLagNodeLabel } from "@utils/node/labelValidation"

// BE stores one entry per tag value, form groups them so a tag can hold multiple values
export const groupGraphTagsByName = (
  graphTags: NodeGraphTagDto[],
): NonNullable<NodeFormValues["graph_tags"]> => {
  const valuesByName = new Map<string, string[]>()

  graphTags.forEach((tag) => {
    valuesByName.set(tag.Name, [...(valuesByName.get(tag.Name) ?? []), tag.Value])
  })

  return Array.from(valuesByName.entries()).map(([tag, values]) => ({ tag, values }))
}

export const flattenGraphTagsFormValues = (
  graphTags: NonNullable<NodeFormValues["graph_tags"]>,
): NodeGraphTagDto[] =>
  graphTags.flatMap((tagRow) => tagRow.values.map((value) => ({ Name: tagRow.tag, Value: value })))

export const getNodeFormDefaultValues = (node: AppNode, allNodes: AppNode[]): NodeFormValues => {
  let expression = ""
  let sampleValue: number | null = null
  let unit: NodeFormValues["unit"] = {
    isEmpty: true,
    decimalPlaces: "2",
    suffix: "",
    usePercentageValue: false,
  }
  let historiMnemonic = ""
  let forecastMnemonic = ""
  let scenarioMnemonic = ""
  let description = ""
  let color = ""
  let properties: NodeFormValues["properties"] = []
  let attributeProperties: NodeFormValues["attributeProperties"] = {
    index: [],
    columns: [],
    data: [],
  }
  let link = ""
  let categoryDisplayNames: NodeFormValues["categoryDisplayNames"] = {}
  let tags: NodeFormValues["tags"] = []
  let graphTags: NodeFormValues["graph_tags"] = []
  let exportExcluded = false

  if (node.type === NodeType.Table) {
    attributeProperties = node.data.originalAttribute.properties
      ? {
          index: node.data.originalAttribute.properties.index,
          columns: node.data.originalAttribute.properties.columns,
          data: node.data.originalAttribute.properties.data,
        }
      : {
          index: ["row1", "row2"],
          columns: ["col1", "col2"],
          data: [
            ["0", "0"],
            ["0", "0"],
          ],
        }
  }
  if (node.type === NodeType.ScenarioTable || node.type === NodeType.ForecastTable) {
    attributeProperties = node.data.originalAttribute.properties
      ? {
          index: node.data.originalAttribute.properties.index,
          columns: node.data.originalAttribute.properties.columns,
          data: node.data.originalAttribute.properties.data,
        }
      : {
          index: ["row1"],
          columns: ["col1"],
          data: [[""]],
        }
  }

  if (node.type === NodeType.Group) {
    color = node.data.metaGroup.color
  } else {
    tags = (node.data.metaNode.tags ?? []).map((tag) => ({ tag: tag.Name, value: tag.Value }))
    graphTags = groupGraphTagsByName(node.data.metaNode.graph_tags ?? [])
    const unitFormatString = node.data.metaNode["Unit Format"] ?? ""
    exportExcluded = node.data.metaNode.exportExcluded ?? false

    // If unit is invalid, we will treat it as empty with default values.
    // User will see a warning and will be able to correct it in the form.
    // This is due to potential legacy nodes having unsupported unit formats.
    unit = isValidUnitFormat(unitFormatString)
      ? decodeNodeUnit(unitFormatString)
      : { isEmpty: true, decimalPlaces: "2", suffix: "", usePercentageValue: false }

    link = !isPseudoNodeType(node.type) ? node.data.metaNode.Link ?? "" : ""
    historiMnemonic = node.data.metaNode["Historic Mnem"] ?? ""
    description = node.data.metaNode.Description ?? ""
  }

  if (node.type === NodeType.Deterministic) {
    expression = node.data.originalNode.properties.df_cpd?.expression?.[0] ?? ""
  } else if (node.type === NodeType.Lag) {
    const hasLagParent = doesNodeHaveLagParent(node, allNodes)

    if (hasLagParent) {
      expression = node.data.originalNode.properties.df_cpd?.expression?.[0] ?? ""
    } else {
      if (node.data.originalNode.properties.a_sample_value !== null) {
        sampleValue = Number(node.data.originalNode.properties.a_sample_value)
      }
    }
  }

  if (
    node.type === NodeType.Continuous ||
    node.type === NodeType.Categorical ||
    node.type === NodeType.Discrete ||
    node.type === NodeType.Mixture
  ) {
    const dfCpd = node.data.originalNode.properties.df_cpd
    const distributions = dfCpd.distribution ?? []
    const propertiesNames = Object.keys(dfCpd)

    properties = distributions.map((_distribution, i) => {
      const entries = propertiesNames.map((property) => [property, dfCpd[property][i] ?? ""])
      return Object.fromEntries(entries)
    })

    if (node.type === NodeType.Categorical) {
      categoryDisplayNames = getCategoryDisplayNames(node.data.originalNode.properties)
    }
  }

  if (node.type === NodeType.Forecast) {
    forecastMnemonic = node.data.metaNode["Forecast Mnem"] ?? ""
  } else if (node.type === NodeType.Scenario) {
    scenarioMnemonic = node.data.metaNode["Scenario Mnem"] ?? ""
  }

  let label = node.data.label ?? ""
  let lagValue = 1

  if (node.type === NodeType.Lag) {
    const splittedLabel = splitLagNodeLabel(node.data.label)
    label = splittedLabel.label
    lagValue = splittedLabel.lagValue
  }

  return {
    label,
    lagValue,
    type: node.type,
    comments: node.data.comments,
    expression,
    unit,
    historic_mnemonic: historiMnemonic,
    forecast_mnemonic: forecastMnemonic,
    scenario_mnemonic: scenarioMnemonic,
    description,
    properties,
    categoryDisplayNames,
    sample_value: sampleValue,
    attributeProperties,
    color,
    link,
    tags,
    graph_tags: graphTags,
    exportExcluded,
  }
}

export const getUpdatedCategoricalNodeProperties = (
  nodeProperties: NodeProperties,
  formPropertiesValues: NodeFormValues["properties"],
  categoryDisplayNames: NodeFormValues["categoryDisplayNames"],
): NodeProperties => {
  if (!formPropertiesValues) return nodeProperties

  const updatedProperties = structuredClone(nodeProperties)
  updatedProperties.df_cpd = {}
  const updatedCategoryDisplayNames = getCategoryDisplayNames(nodeProperties, categoryDisplayNames)

  formPropertiesValues.forEach((propertiesRow, i) => {
    Object.entries(propertiesRow).forEach(([key, value]) => {
      const updatedKey =
        key === "distribution" || updatedProperties.parents_lst.includes(key)
          ? key
          : getCategoryDisplayName(updatedCategoryDisplayNames, key) ?? key

      if (!updatedProperties.df_cpd[updatedKey]) updatedProperties.df_cpd[updatedKey] = []

      updatedProperties.df_cpd[updatedKey][i] = value
    })
  })

  return updatedProperties
}

export const getCategoricalCategoryKeys = (
  nodeProperties: Pick<BaseGraphZipNodeProperties, "df_cpd" | "parents_lst">,
): string[] => {
  return Object.keys(nodeProperties.df_cpd).filter(
    (key) => key !== "distribution" && !(nodeProperties.parents_lst ?? []).includes(key),
  )
}

export const getCategoryDisplayName = (
  currentDisplayNames: NodeFormValues["categoryDisplayNames"],
  key: string,
): string | undefined => {
  const displayName = Object.entries(currentDisplayNames).find(
    ([displayNameKey]) => displayNameKey === key,
  )?.[1]

  return typeof displayName === "string" ? displayName : undefined
}

export const getCategoryDisplayNames = (
  nodeProperties: Pick<BaseGraphZipNodeProperties, "df_cpd" | "parents_lst">,
  currentDisplayNames: NodeFormValues["categoryDisplayNames"] = {},
): NodeFormValues["categoryDisplayNames"] => {
  return Object.fromEntries(
    getCategoricalCategoryKeys(nodeProperties).map((key) => [
      key,
      getCategoryDisplayName(currentDisplayNames, key) ?? key,
    ]),
  )
}

export const getDistributionParamsFromAllRows = (
  formPropertiesValues: NodeFormValues["properties"],
  distributionMap: DistributionMap,
): string[] => {
  const keys = new Set<DistributionParam>()
  for (const formPropValue of formPropertiesValues) {
    const distribution = distributionMap[formPropValue.distribution as Distribution]
    if (!distribution) continue

    for (const property of Object.values(distribution)) keys.add(property)
  }
  return [...Array.from(keys), "distribution"]
}

/**
 * Value to store for a distribution param the form left empty.
 *
 * An empty field normally means "not filled in yet", so the distribution default takes
 * over. `ubound` on a distribution with unbounded support is the exception: clearing it
 * is a deliberate choice — a node with no children needs no upper cut — and putting the
 * default back would hand the node states nobody asked for. The form already refuses an
 * empty `ubound` once the node has a child (see `getUnboundedSupportUboundSchema`).
 */
const getClearedParamValue = (
  param: DistributionParam,
  distribution: Distribution,
  value: string | null | undefined,
) => {
  if (value !== "" && value !== null) return value

  const hasUnboundedSupport =
    UNBOUNDED_SUPPORT_FLOOR_OFFSET[distribution as Distributions] !== undefined
  if (param === DistributionParam.ubound && hasUnboundedSupport) return null

  return distributionDefaultValues[distribution]?.[param]
}

export const getUpdatedNodeProperties = (
  nodeProperties: NodeProperties,
  formPropertiesValues: NodeFormValues["properties"],
  distributionMap: DistributionMap,
): NodeProperties => {
  if (!formPropertiesValues) return nodeProperties

  const allRowsDistributionParams = getDistributionParamsFromAllRows(
    formPropertiesValues,
    distributionMap,
  )
  const updatedProperties = structuredClone(nodeProperties)
  const distributions = formPropertiesValues.map((v) => v.distribution as Distribution)

  updatedProperties.df_cpd = { distribution: distributions }

  formPropertiesValues.forEach((propertiesRow, rowIndex) => {
    const rowDistributionParams = getDistributionParamsFromAllRows([propertiesRow], distributionMap)

    for (const param of Object.keys(propertiesRow)) {
      const existingValue = propertiesRow[param as DistributionParam | "distribution"]

      const isParentParam = updatedProperties.parents_lst.includes(param)
      if (isParentParam) {
        if (!updatedProperties.df_cpd[param]) updatedProperties.df_cpd[param] = []
        updatedProperties.df_cpd[param][rowIndex] = existingValue
        continue
      }

      const isDistributionParam = allRowsDistributionParams.includes(param)
      if (!isDistributionParam) continue

      if (!updatedProperties.df_cpd[param]) updatedProperties.df_cpd[param] = []

      const isRowDistributionParam = rowDistributionParams.includes(param)
      const rowDistribution = propertiesRow.distribution as Distribution

      if (isRowDistributionParam) {
        updatedProperties.df_cpd[param][rowIndex] = getClearedParamValue(
          param as DistributionParam,
          rowDistribution,
          existingValue,
        )
      } else updatedProperties.df_cpd[param][rowIndex] = null
    }
  })

  // add missing params that may need to be added due to changing distribution
  allRowsDistributionParams.forEach((param) => {
    if (updatedProperties.df_cpd[param]) return

    updatedProperties.df_cpd[param] = distributions.map(
      (distribution) =>
        distributionDefaultValues[distribution]?.[param as DistributionParam] ?? null,
    )
  })

  return updatedProperties
}

export const convertNodeBasedOnFormValues = (
  node: AppNode,
  allNodes: AppNode[],
  formData: NodeFormValues,
  parentsLinkedNodesMap: Map<string, TKNode>,
): AppNode => {
  const updatedNode = structuredClone(node)
  const updatedType = formData.type as Exclude<NodeType, NodeType.Group>

  const label =
    updatedType === NodeType.Lag
      ? createLagNodeLabel(formData.label, formData.lagValue ?? 1)
      : formData.label

  updatedNode.data.label = label
  updatedNode.data.comments = formData.comments ?? ""

  if (updatedNode.type === NodeType.Group) {
    updatedNode.data.metaGroup.color = formData.color ?? ""
    updatedNode.data.metaGroup.label = formData.label
    return updatedNode
  } else {
    updatedNode.data.metaNode["Historic Mnem"] = formData.historic_mnemonic ?? ""
    updatedNode.data.metaNode["Forecast Mnem"] = formData.forecast_mnemonic ?? ""
    updatedNode.data.metaNode["Scenario Mnem"] = formData.scenario_mnemonic ?? ""
    updatedNode.data.metaNode["Description"] = formData.description ?? ""
    updatedNode.data.metaNode.label = label
    updatedNode.data.metaNode.Link = !isPseudoNodeType(updatedNode.type) ? formData.link : ""
    updatedNode.data.metaNode.tags = (formData.tags ?? []).map((tag) => ({
      Name: tag.tag,
      Value: tag.value,
    }))
    updatedNode.data.metaNode.graph_tags = flattenGraphTagsFormValues(formData.graph_tags ?? [])
    updatedNode.data.metaNode.exportExcluded = formData.exportExcluded ?? false

    if (
      formData.unit.isEmpty &&
      !isValidUnitFormat(updatedNode.data.metaNode["Unit Format"] ?? "")
    ) {
      // this case means user still didn't fixed their invalid unit format, we keep it as is to avoid data loss
    } else {
      updatedNode.data.metaNode["Unit Format"] = encodeNodeUnit(formData.unit)
    }

    // we don't care about other nodes potentially being changed, we want to have changes of that particular node to updated form
    const convertedNode = convertNode(
      updatedNode,
      updatedType,
      allNodes,
      parentsLinkedNodesMap,
    ).find((n) => n.id === updatedNode.id)

    // that means converted node is the same as it was originally in list of "allNodes"
    if (!convertedNode) {
      return allNodes.find(({ id }) => id === updatedNode.id)!
    }

    if (node.type !== updatedType) return convertedNode

    if (convertedNode.type === NodeType.Deterministic) {
      convertedNode.data.originalNode.properties.df_cpd.expression = [formData.expression ?? ""]
    } else if (convertedNode.type === NodeType.Discrete) {
      const nodeProperties = convertedNode.data.originalNode.properties
      const updatedProperties = getUpdatedNodeProperties(
        nodeProperties,
        formData.properties,
        discreteNodeDistributionProperties,
      )
      convertedNode.data.originalNode.properties = updatedProperties
    } else if (
      convertedNode.type === NodeType.Continuous ||
      convertedNode.type === NodeType.Mixture
    ) {
      const nodeProperties = convertedNode.data.originalNode.properties
      const updatedProperties = getUpdatedNodeProperties(
        nodeProperties,
        formData.properties,
        continuousNodeDistributionProperties,
      )
      convertedNode.data.originalNode.properties = updatedProperties
    } else if (convertedNode.type === NodeType.Categorical) {
      const nodeProperties = convertedNode.data.originalNode.properties
      const updatedProperties = getUpdatedCategoricalNodeProperties(
        nodeProperties,
        formData.properties,
        formData.categoryDisplayNames,
      )
      convertedNode.data.originalNode.properties = updatedProperties
    } else if (convertedNode.type === NodeType.Lag) {
      const hasLagParent = doesNodeHaveLagParent(convertedNode, allNodes)

      if (convertedNode.data.metaNode["Historic Mnem"]) {
        convertedNode.data.originalNode.properties.df_cpd.expression = [""]
        convertedNode.data.originalNode.properties.a_sample_value = null
      } else if (hasLagParent) {
        const expression = formData.expression ?? ""
        convertedNode.data.originalNode.properties.df_cpd.expression = [expression]
        convertedNode.data.metaNode["Historic Mnem"] = ""
      } else {
        const sampleValue = formData.sample_value ?? null
        convertedNode.data.originalNode.properties.a_sample_value = sampleValue
        convertedNode.data.metaNode["Historic Mnem"] = ""
      }
    } else if (convertedNode.type === NodeType.Forecast) {
      updatedNode.data.metaNode["Forecast Mnem"] = formData.forecast_mnemonic ?? ""
    } else if (convertedNode.type === NodeType.Scenario) {
      updatedNode.data.metaNode["Scenario Mnem"] = formData.scenario_mnemonic ?? ""
    } else if (
      convertedNode.type === NodeType.Table ||
      convertedNode.type === NodeType.ForecastTable ||
      convertedNode.type === NodeType.ScenarioTable
    ) {
      const { columns, data, index } = formData.attributeProperties
      convertedNode.data.originalAttribute.properties.columns = columns
      convertedNode.data.originalAttribute.properties.data = data
      convertedNode.data.originalAttribute.properties.index = index
    }

    return convertedNode
  }
}

export const isUniqueNameForRename = (
  newName: string,
  existingNames: string[],
  ignoredIndex?: number,
): boolean => {
  const normalizedName = newName.trim()
  const comparableNames =
    ignoredIndex !== undefined
      ? existingNames.filter((_existingName, index) => index !== ignoredIndex)
      : existingNames

  return Boolean(normalizedName) && !comparableNames.includes(newName)
}

// True when some run of consecutive words in the label starts with `text`, e.g. "United Kingdom"
// matches label "demand United Kingdom" since a word boundary match exists anywhere in the label.
// Prevents matching tags like "US" inside "Business"
const startsAtWordBoundary = (label: string, text: string): boolean => {
  const normalizedText = text.trim().toLowerCase()
  if (!normalizedText) return false

  const words = label.toLowerCase().split(" ").filter(Boolean)

  return words.some((_, index) => words.slice(index).join(" ").startsWith(normalizedText))
}

export const getMatchingNodeTags = (
  text: string,
  allTagsRows: NodeTagRow[],
  appliedTags: NonNullable<NodeFormValues["tags"]>,
): NodeTagRow[] => {
  return allTagsRows.filter((tagRow) => {
    const isAlreadyApplied = appliedTags.some((appliedTag) => appliedTag.tag === tagRow.Name)
    if (isAlreadyApplied) return false

    return (
      startsAtWordBoundary(text, tagRow.Value) ||
      startsAtWordBoundary(text, tagRow.Description ?? "")
    )
  })
}

export const getMatchingGraphTags = (
  text: string,
  allGraphTags: GraphTagDto[],
  appliedGraphTags: NonNullable<NodeFormValues["graph_tags"]>,
): GraphTagDto[] => {
  return allGraphTags.filter((tagRow) => {
    const appliedValues = appliedGraphTags.find((tag) => tag.tag === tagRow.Name)?.values ?? []
    if (appliedValues.includes(tagRow.Value)) return false

    return startsAtWordBoundary(text, tagRow.Value)
  })
}

/** Errors belonging to a specific tag row. */
export const getTagErrors = (
  errors: ValidationState["errors"],
  tagName: string,
): ValidationState["errors"] => errors.filter((error) => error.meta?.tag?.name === tagName)

/** Errors for mandatory graph-level tags that aren't attached to the node at all (no row to attach them to). */
export const getMissingRequiredTagErrors = (
  errors: ValidationState["errors"],
): ValidationState["errors"] => errors.filter((error) => error.meta?.tag?.issue === "required")
